import {
  AnyAction,
  createSlice,
  PayloadAction,
  Reducer,
  Slice,
} from '@reduxjs/toolkit';

import { PaginationDefaults } from '../../../../../../../shared/utils/pagination-defaults.util';
import { FiltersPayloadModel } from '../../../../../../shared/models/filters-payload.model';
import { getReviewsFiltersValues } from '../../../thunks/get-reviews-filters.thunk';
import { getReviewsStats } from '../../../thunks/get-reviews-stats.thunk';
import { getReviewsTotalCount } from '../../../thunks/get-reviews-total-count.thunk';
import { getReviews } from '../../../thunks/get-reviews.thunk';
import { ReviewsFilters } from '../../../thunks/models/reviews-filters.model';
import { ReviewsHeaderAndPaginationFilters } from '../../../thunks/models/reviews-header-and-pagination-filters.model';
import { ReviewsStateModel } from '../models/reviews-state.model';
import { ReviewsReducers } from '../types/reviews-reducers.type';

const filtersInitialState: ReviewsHeaderAndPaginationFilters = {
  header: {
    search: '',
    date: '',
    isFeatured: undefined,
    platform: '',
    rating: '',
    release: '',
    vehicle: '',
  },
  pagination: {
    totalCount: PaginationDefaults.totalCount,
    pageNumber: PaginationDefaults.pageNumber,
    size: PaginationDefaults.size,
    offset: PaginationDefaults.offset,
  },
};

const initialState: ReviewsStateModel = {
  reviews: [],
  filters: filtersInitialState,
  stats: null,
  filtersDropdownValues: { releases: [], vehicles: [], platforms: [] },
  areReviewsFiltersVisible: false,
};

export const reviewsSlice: Slice<
  ReviewsStateModel,
  ReviewsReducers,
  'reviews'
> = createSlice({
  name: 'reviews',
  initialState,
  reducers: {
    resetReviewsSliceState(state) {
      Object.assign(state, initialState);
    },
    addPagination(state, action) {
      state.filters.pagination.size = action.payload.size;
      state.filters.pagination.offset = action.payload.offset;
      state.filters.pagination.pageNumber = action.payload.pageNumber;
    },

    resetPagination(state, action) {
      state.filters.pagination.offset = PaginationDefaults.offset;
      state.filters.pagination.size = action.payload.size;
      state.filters.pagination.pageNumber = PaginationDefaults.pageNumber;
    },

    setAppId(state, action) {
      state.appId = action.payload;
    },

    filterReviewsByAuthorAndReview(
      state,
      { payload }: PayloadAction<FiltersPayloadModel<ReviewsFilters>>
    ) {
      state.filters.header = {
        ...state.filters.header,
        search: payload.filterValue,
      };
    },

    filterReviewsByVehicle(
      state,
      { payload }: PayloadAction<string | undefined>
    ) {
      state.filters.header = {
        ...state.filters.header,
        vehicle: payload,
      };
    },

    filterReviewsByRelease(
      state,
      { payload }: PayloadAction<string | undefined>
    ) {
      state.filters.header = {
        ...state.filters.header,
        release: payload,
      };
    },

    filterReviewsByPlatform(
      state,
      { payload }: PayloadAction<string | undefined>
    ) {
      state.filters.header = {
        ...state.filters.header,
        platform: payload,
      };
    },

    filterReviewsByRating(
      state,
      { payload }: PayloadAction<string | undefined>
    ) {
      state.filters.header = {
        ...state.filters.header,
        rating: payload,
      };
    },

    filterReviewsByIsFeatured(
      state,
      { payload }: PayloadAction<boolean | undefined>
    ) {
      state.filters.header = {
        ...state.filters.header,
        isFeatured: payload,
      };
    },

    filterReviewsByDate(state, { payload }: PayloadAction<string | undefined>) {
      state.filters.header = {
        ...state.filters.header,
        date: payload,
      };
    },

    reviewsToggleFiltersVisibility(state) {
      state.areReviewsFiltersVisible = !state.areReviewsFiltersVisible;
    },

    clearReviewsFilters(state) {
      state.filters = filtersInitialState;
    },
  },

  extraReducers: builder => {
    builder
      .addCase(getReviews.fulfilled, (state, action) => {
        state.reviews = action.payload;
      })
      .addCase(getReviewsTotalCount.fulfilled, (state, action) => {
        state.filters.pagination.totalCount = action.payload;
      })
      .addCase(getReviewsStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(getReviewsFiltersValues.fulfilled, (state, action) => {
        state.filtersDropdownValues = action.payload;
      });
  },
});

export const {
  addPagination,
  resetPagination,
  setAppId,
  filterReviewsByVehicle,
  filterReviewsByRelease,
  filterReviewsByPlatform,
  filterReviewsByRating,
  filterReviewsByIsFeatured,
  filterReviewsByDate,
  reviewsToggleFiltersVisibility,
  clearReviewsFilters,
  filterReviewsByAuthorAndReview,
} = reviewsSlice.actions;

const reviewsSliceReducers: Reducer<ReviewsStateModel, AnyAction> =
  reviewsSlice.reducer;

export default reviewsSliceReducers;
